import React from "react";
import { Users, BookOpen, Calendar, MessageCircle } from "lucide-react";
import Image from "next/image";
import { z } from "zod";
import Link from "next/link";

const FeatureSchema = z.object({
	icon: z.any(),
	title: z.string(),
	description: z.string(),
	color: z.string(),
});

const ImageSchema = z.object({
	src: z.string(),
	alt: z.string(),
});

const LiveDiscussionSchema = z.object({
	title: z.string(),
	topic: z.string(),
});

const CommunitySchema = z.object({
	header: z.string().default("Join the Global Architect Community"),
	description: z
		.string()
		.default(
			"Connect with 5,000+ architects worldwide. Share knowledge, learn from experts, and grow your professional network in our thriving community hub."
		),
	buttonText: z.string().default("Join 5,000+ Architects"),
	image: ImageSchema.default({
		src: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop",
		alt: "Virtual architect roundtable",
	}),
	liveDiscussion: LiveDiscussionSchema.default({
		title: "Live Discussion",
		topic: "ISO 19650 Implementation",
	}),
	features: z.array(FeatureSchema).default([
		{
			icon: MessageCircle,
			title: "Expert Forums",
			description: "Get answers to your BIM questions from experienced professionals.",
			color: "bg-primary-500",
		},
		{
			icon: Calendar,
			title: "Live Webinars",
			description: "Weekly sessions on latest BIM trends and best practices.",
			color: "bg-secondary-500",
		},
		{
			icon: BookOpen,
			title: "Free Courses",
			description: "Access our library of BIM tutorials and certification courses.",
			color: "bg-accent-500",
		},
	]),
});

type CommunityProps = z.input<typeof CommunitySchema>;

const Community: React.FC<CommunityProps> = (props = {}) => {
	const data = CommunitySchema.parse(props);
	return (
		<section id="community" className="bg-gradient-to-r from-secondary-50 to-primary-50 py-20">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="grid items-center gap-12 lg:grid-cols-2">
					<div>
						<h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-4xl">{data.header}</h2>
						<p className="mb-8 text-xl text-gray-600">{data.description}</p>

						<div className="mb-8 space-y-6">
							{data.features.map((feature, index) => (
								<div key={index} className="flex items-start space-x-4">
									<div
										className={`flex h-12 w-12 items-center justify-center rounded-lg ${feature.color}`}
									>
										<feature.icon className="h-6 w-6 text-white" />
									</div>
									<div>
										<h3 className="mb-2 font-semibold text-gray-900">{feature.title}</h3>
										<p className="text-gray-600">{feature.description}</p>
									</div>
								</div>
							))}
						</div>

						<Link href={`https://www.proptechbuzz.com/communities/bim-digital-twin`}>
							<button className="flex items-center space-x-2 rounded-lg bg-secondary-500 px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-secondary-600">
								<Users className="h-5 w-5" />
								<span>{data.buttonText}</span>
							</button>
						</Link>
					</div>

					<div className="relative">
						<Image
							src={data.image.src}
							alt={data.image.alt}
							className="rounded-xl shadow-2xl"
							width={600}
							height={400}
						/>
						<div className="absolute -bottom-4 -left-4 rounded-lg bg-white p-4 shadow-lg">
							<div className="flex items-center space-x-2">
								<div className="h-3 w-3 animate-pulse rounded-full bg-green-500"></div>
								<span className="text-sm font-semibold text-gray-900">
									{data.liveDiscussion.title}
								</span>
							</div>
							<p className="mt-1 text-sm text-gray-600">{data.liveDiscussion.topic}</p>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default Community;
